import { useEffect, useState } from "react";
import { Me, setAuth, Wallet } from "@/lib/api";
import { Link, navigate, useRoute } from "@/lib/router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, LogOut, ShieldCheck, Coins, UserRound, Eye, Download, Bell, Inbox } from "lucide-react";
import { Pagination } from "@/components/Pagination";
import { validatePassword, PASSWORD_HINT } from "@/lib/password";
import { WalletPage } from "./WalletPage";
import { SubscriptionsTab } from "./SubscriptionsPage";
import { InboxTab } from "./InboxPage";

type Tab = "profile" | "security" | "wallet" | "subscriptions" | "inbox" | "views" | "downloads";

const NAV: Array<{ key: Tab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { key: "profile",       label: "资料 / 会员",    icon: UserRound },
  { key: "security",      label: "账户安全",       icon: ShieldCheck },
  { key: "wallet",        label: "积分中心",       icon: Coins },
  { key: "subscriptions", label: "订阅推送",       icon: Bell },
  { key: "inbox",         label: "站内信",         icon: Inbox },
  { key: "views",         label: "浏览历史",       icon: Eye },
  { key: "downloads",     label: "下载历史",       icon: Download },
];

export function MePage() {
  const route = useRoute();
  const initialTab = (route.query.tab as Tab) || "profile";
  const [me, setMe] = useState<any>(null);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [wallet, setWallet] = useState<{ points: number } | null>(null);

  // 路由 query 变化 → tab 同步
  useEffect(() => {
    const t = (route.query.tab as Tab) || "profile";
    if (["profile","security","wallet","subscriptions","inbox","views","downloads"].includes(t)) setTab(t);
  }, [route.query.tab]);

  const switchTab = (t: Tab) => {
    setTab(t);
    navigate("/me", { tab: t });
  };

  const reload = () => Me.profile().then(setMe).catch(() => navigate("/login"));
  useEffect(() => {
    reload();
    Wallet.get().then(setWallet).catch(() => {});
  }, []);
  if (!me) return <div className="container mx-auto py-16 text-center text-muted-foreground">加载中...</div>;

  const logout = () => { setAuth(null); navigate("/"); };

  return (
    <div className="container mx-auto px-4 py-6 grid lg:grid-cols-[240px_1fr] gap-6">
      <aside className="space-y-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">
                {(me.display_name || me.email || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{me.display_name || me.email || "未命名"}</div>
                <div className="text-xs text-muted-foreground truncate">{me.email || me.phone || "未绑定"}</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-2">
            <nav className="flex flex-col">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = tab === n.key;
                return (
                  <button key={n.key} onClick={() => switchTab(n.key)} type="button"
                    className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors text-left ${
                      active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
                    }`}>
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </button>
                );
              })}
              <div className="my-1 border-t" />
              <button onClick={logout} type="button"
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors text-left text-muted-foreground hover:bg-muted hover:text-foreground">
                <LogOut className="h-4 w-4" />
                退出登录
              </button>
            </nav>
          </CardContent>
        </Card>
      </aside>

      <section>
        {tab === "profile" && <ProfileTab me={me} wallet={wallet} />}
        {tab === "security" && <SecurityTab me={me} onChanged={reload} />}
        {tab === "wallet" && <WalletPage />}
        {tab === "subscriptions" && <SubscriptionsTab />}
        {tab === "inbox" && <InboxTab />}
        {tab === "views" && <ViewHistoryTab />}
        {tab === "downloads" && <DownloadHistoryTab />}
      </section>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return <div><div className="text-xs text-muted-foreground">{label}</div><div className="mt-0.5">{value}</div></div>;
}

function ProfileTab({ me, wallet }: { me: any; wallet: { points: number } | null }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>基本资料</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="账户类型" value={me.account_type === "enterprise" ? <Badge>企业</Badge> : <Badge variant="secondary">个人</Badge>} />
            <Field label="邮箱" value={me.email || "未绑定"} />
            <Field label="手机" value={me.phone || "未绑定"} />
            <Field label="昵称" value={me.display_name || "-"} />
            {me.account_type === "enterprise" && (
              <>
                <Field label="公司名称" value={me.company_name || "-"} />
                <Field label="统一社会信用代码" value={me.unified_credit_code || "-"} />
                <Field label="联系人" value={me.contact_name || "-"} />
              </>
            )}
            <Field label="角色" value={
              <span className="flex flex-wrap gap-1">
                {me.roles.map((r: string) => <Badge key={r} variant="secondary">{r}</Badge>)}
              </span>
            } />
            <Field label="注册时间" value={new Date(me.created_at).toLocaleString()} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Crown className="h-4 w-4 text-amber-500" /> 会员状态</CardTitle></CardHeader>
        <CardContent>
          {me.memberships?.length ? (
            <ul className="space-y-2">
              {me.memberships.map((m: any, i: number) => (
                <li key={i} className="flex justify-between text-sm">
                  <span className="font-medium">{m.name}</span>
                  <span className="text-muted-foreground">{m.expires_at ? `到期 ${new Date(m.expires_at).toLocaleDateString()}` : "永久"}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground">尚未开通会员，<Link to="/pricing" className="text-primary hover:underline">查看套餐</Link></div>
          )}
        </CardContent>
      </Card>

      {wallet && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Coins className="h-4 w-4 text-amber-500" /> 积分概览</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="text-3xl font-semibold">{wallet.points}</div>
              <div className="text-sm text-muted-foreground">可用于解锁资源、产品优先使用</div>
            </div>
            <div className="mt-3 text-right">
              <Link to="/me" query={{ tab: "wallet" }} className="text-sm text-primary hover:underline">查看明细 / 充值 →</Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ViewHistoryTab() {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  useEffect(() => {
    Me.viewHistory(page, PAGE_SIZE).then((r) => { setItems(r.items); setTotal(r.total); }).catch(() => {});
  }, [page]);
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-4 w-4" /> 浏览历史</CardTitle></CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">暂无记录</div>
        ) : (
          <>
            <ul className="divide-y">
              {items.map((r) => (
                <li key={r.project_id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <Link to={`/p/${r.slug}`} className="font-medium hover:text-primary line-clamp-1 flex-1">{r.title}</Link>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground shrink-0">
                    <Badge variant="outline">{r.category?.name}</Badge>
                    <span>{r.view_count} 次</span>
                    <span>{new Date(r.viewed_at).toLocaleString()}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4"><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /></div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DownloadHistoryTab() {
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  useEffect(() => {
    Me.downloadHistory(page, PAGE_SIZE).then((r) => { setItems(r.items); setTotal(r.total); }).catch(() => {});
  }, [page]);
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Download className="h-4 w-4" /> 下载历史</CardTitle></CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground">暂无记录</div>
        ) : (
          <>
            <ul className="divide-y">
              {items.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{r.filename}</div>
                    <Link to={`/p/${r.slug}`} className="text-xs text-muted-foreground hover:text-primary line-clamp-1">来自：{r.title}</Link>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">
                    {new Date(r.downloaded_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4"><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /></div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SecurityTab({ me, onChanged }: { me: any; onChanged: () => void }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> 修改密码</CardTitle></CardHeader>
        <CardContent><ChangePasswordForm /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>绑定邮箱</CardTitle></CardHeader>
        <CardContent>
          <div className="text-sm mb-3">
            当前: {me.email
              ? <span className="font-medium">{me.email}{me.email_verified_at ? <Badge variant="secondary" className="ml-2">已验证</Badge> : null}</span>
              : <span className="text-muted-foreground">未绑定</span>}
          </div>
          <BindEmailForm onChanged={onChanged} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>绑定手机</CardTitle></CardHeader>
        <CardContent>
          <div className="text-sm mb-3">
            当前: {me.phone
              ? <span className="font-medium">{me.phone}{me.phone_verified_at ? <Badge variant="secondary" className="ml-2">已验证</Badge> : null}</span>
              : <span className="text-muted-foreground">未绑定</span>}
          </div>
          <BindPhoneForm onChanged={onChanged} />
        </CardContent>
      </Card>
    </div>
  );
}

function ChangePasswordForm() {
  const [oldP, setOldP] = useState("");
  const [newP, setNewP] = useState("");
  const [confirmP, setConfirmP] = useState("");
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(""); setErr("");
    if (newP !== confirmP) { setErr("两次输入的新密码不一致"); return; }
    const ruleErr = validatePassword(newP);
    if (ruleErr) { setErr(ruleErr); return; }
    setLoading(true);
    try {
      await Me.changePassword(oldP, newP);
      setMsg("密码已更新"); setOldP(""); setNewP(""); setConfirmP("");
    } catch (e: any) { setErr(e?.body?.message || (e?.status === 401 ? "原密码错误" : "提交失败")); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
      <div className="space-y-1.5 md:col-span-2">
        <Label>原密码</Label>
        <Input type="password" required value={oldP} onChange={(e) => setOldP(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>新密码</Label>
        <Input type="password" required minLength={8} value={newP} onChange={(e) => setNewP(e.target.value)} />
      </div>
      <div className="space-y-1.5">
        <Label>确认新密码</Label>
        <Input type="password" required minLength={8} value={confirmP} onChange={(e) => setConfirmP(e.target.value)} />
      </div>
      <p className="md:col-span-2 text-xs text-muted-foreground">{PASSWORD_HINT}</p>
      {err && <p className="md:col-span-2 text-sm text-destructive">{err}</p>}
      {msg && <p className="md:col-span-2 text-sm text-emerald-600">{msg}</p>}
      <div className="md:col-span-2"><Button type="submit" disabled={loading}>{loading ? "提交中..." : "修改密码"}</Button></div>
    </form>
  );
}

function BindEmailForm({ onChanged }: { onChanged: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    if (!email) { setErr("请先输入邮箱"); return; }
    setErr(""); setMsg(""); setLoading(true);
    try { await Me.bindEmailRequest(email); setSent(true); setMsg("验证码已发送"); }
    catch (e: any) { setErr(e?.body?.message || "发送失败"); }
    finally { setLoading(false); }
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try { await Me.bindEmailVerify(email, code); setMsg("邮箱绑定成功"); setSent(false); setCode(""); onChanged(); }
    catch (e: any) { setErr(e?.body?.message || "验证失败"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={verify} className="max-w-xl space-y-2">
      <div className="flex flex-col md:flex-row gap-2 items-end">
        <div className="space-y-1.5 flex-1 min-w-0">
          <Label>新邮箱</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
        </div>
        <div className="space-y-1.5 w-full md:w-32 shrink-0">
          <Label>验证码</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} disabled={!sent} />
        </div>
        <Button type="button" variant="outline" onClick={sendCode} disabled={loading} className="shrink-0">{sent ? "重发" : "发送验证码"}</Button>
        <Button type="submit" disabled={loading || !sent || !code} className="shrink-0">绑定</Button>
      </div>
      {err && <p className="text-sm text-destructive">{err}</p>}
      {msg && <p className="text-sm text-emerald-600">{msg}</p>}
    </form>
  );
}

function BindPhoneForm({ onChanged }: { onChanged: () => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const sendCode = async () => {
    if (!phone) { setErr("请先输入手机号"); return; }
    setErr(""); setMsg(""); setLoading(true);
    try { await Me.bindPhoneRequest(phone); setSent(true); setMsg("验证码已发送"); }
    catch (e: any) { setErr(e?.body?.message || "发送失败"); }
    finally { setLoading(false); }
  };
  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(""); setMsg(""); setLoading(true);
    try { await Me.bindPhoneVerify(phone, code); setMsg("手机绑定成功"); setSent(false); setCode(""); onChanged(); }
    catch (e: any) { setErr(e?.body?.message || "验证失败"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={verify} className="max-w-xl space-y-2">
      <div className="flex flex-col md:flex-row gap-2 items-end">
        <div className="space-y-1.5 flex-1 min-w-0">
          <Label>新手机号</Label>
          <Input type="tel" required value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="space-y-1.5 w-full md:w-32 shrink-0">
          <Label>验证码</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} disabled={!sent} />
        </div>
        <Button type="button" variant="outline" onClick={sendCode} disabled={loading} className="shrink-0">{sent ? "重发" : "发送验证码"}</Button>
        <Button type="submit" disabled={loading || !sent || !code} className="shrink-0">绑定</Button>
      </div>
      {err && <p className="text-sm text-destructive">{err}</p>}
      {msg && <p className="text-sm text-emerald-600">{msg}</p>}
    </form>
  );
}
